import { type Vec2, wrapIndex } from '../core/math';

/** Andrew's monotone chain. Returns a counter-clockwise hull. */
export function convexHull(points: Vec2[]): Vec2[] {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  if (pts.length < 3) return pts;

  const turn = (o: Vec2, a: Vec2, b: Vec2): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

  const build = (seq: Vec2[]): Vec2[] => {
    const stack: Vec2[] = [];
    for (const p of seq) {
      while (stack.length >= 2 && turn(stack[stack.length - 2], stack[stack.length - 1], p) <= 0) {
        stack.pop();
      }
      stack.push(p);
    }
    stack.pop();
    return stack;
  };

  return build(pts).concat(build([...pts].reverse()));
}

/** Uniform Catmull-Rom through a closed ring of control points. */
export function catmullRomClosed(pts: Vec2[], samplesPerSegment: number): Vec2[] {
  const n = pts.length;
  const out: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const p0 = pts[wrapIndex(i - 1, n)];
    const p1 = pts[i];
    const p2 = pts[wrapIndex(i + 1, n)];
    const p3 = pts[wrapIndex(i + 2, n)];
    for (let j = 0; j < samplesPerSegment; j++) {
      const t = j / samplesPerSegment;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push({
        x:
          0.5 *
          (2 * p1.x +
            (-p0.x + p2.x) * t +
            (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 +
            (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y:
          0.5 *
          (2 * p1.y +
            (-p0.y + p2.y) * t +
            (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 +
            (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
      });
    }
  }
  return out;
}

/**
 * Resample a closed polyline to constant arclength spacing. Every downstream
 * stage — curvature, the speed profile, checkpoint placement — assumes a
 * uniform `ds`, which makes the integrals plain sums.
 */
export function resampleUniform(poly: Vec2[], spacing: number): Vec2[] {
  const n = poly.length;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[wrapIndex(i + 1, n)];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }

  const count = Math.max(16, Math.round(total / spacing));
  const step = total / count;
  const out: Vec2[] = [];

  let segIndex = 0;
  let segStart = poly[0];
  let segEnd = poly[wrapIndex(1, n)];
  let segLen = Math.hypot(segEnd.x - segStart.x, segEnd.y - segStart.y);
  let walked = 0;

  for (let i = 0; i < count; i++) {
    const target = i * step;
    while (walked + segLen < target && segIndex < n - 1) {
      walked += segLen;
      segIndex++;
      segStart = poly[segIndex];
      segEnd = poly[wrapIndex(segIndex + 1, n)];
      segLen = Math.hypot(segEnd.x - segStart.x, segEnd.y - segStart.y);
    }
    const t = segLen > 1e-9 ? (target - walked) / segLen : 0;
    out.push({
      x: segStart.x + (segEnd.x - segStart.x) * t,
      y: segStart.y + (segEnd.y - segStart.y) * t,
    });
  }
  return out;
}

/** Laplacian smoothing on a closed ring. Pulls each point toward its neighbours. */
export function smoothClosed(pts: Vec2[], strength: number, passes: number): Vec2[] {
  let cur = pts;
  const n = pts.length;
  for (let p = 0; p < passes; p++) {
    const next: Vec2[] = new Array(n);
    for (let i = 0; i < n; i++) {
      const a = cur[wrapIndex(i - 1, n)];
      const b = cur[i];
      const c = cur[wrapIndex(i + 1, n)];
      next[i] = {
        x: b.x + strength * ((a.x + c.x) * 0.5 - b.x),
        y: b.y + strength * ((a.y + c.y) * 0.5 - b.y),
      };
    }
    cur = next;
  }
  return cur;
}

/**
 * Signed curvature at each point, from the circumscribed circle of each
 * consecutive triple: k = 4*Area / (|ab| |bc| |ca|).
 *
 * No derivatives and no trigonometry, so it is both numerically stable on
 * near-collinear points and safe for cross-engine reproducibility. Sign is
 * positive for a left-hand corner.
 */
export function curvature(pts: Vec2[]): Float64Array {
  const n = pts.length;
  const k = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const a = pts[wrapIndex(i - 1, n)];
    const b = pts[i];
    const c = pts[wrapIndex(i + 1, n)];

    const ab = Math.hypot(b.x - a.x, b.y - a.y);
    const bc = Math.hypot(c.x - b.x, c.y - b.y);
    const ca = Math.hypot(a.x - c.x, a.y - c.y);
    const denom = ab * bc * ca;
    if (denom < 1e-9) {
      k[i] = 0;
      continue;
    }
    const cross = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    k[i] = (2 * cross) / denom;
  }
  return k;
}

/** Moving average over a closed ring, to kill resampling noise in curvature. */
export function smoothSeries(src: Float64Array, halfWidth: number): Float64Array {
  const n = src.length;
  const out = new Float64Array(n);
  const width = halfWidth * 2 + 1;
  for (let i = 0; i < n; i++) {
    let sum = 0;
    for (let j = -halfWidth; j <= halfWidth; j++) sum += src[wrapIndex(i + j, n)];
    out[i] = sum / width;
  }
  return out;
}

/** Unit normals (pointing left of travel) for a closed centerline. */
export function normals(pts: Vec2[]): Vec2[] {
  const n = pts.length;
  const out: Vec2[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const a = pts[wrapIndex(i - 1, n)];
    const b = pts[wrapIndex(i + 1, n)];
    const tx = b.x - a.x;
    const ty = b.y - a.y;
    const len = Math.hypot(tx, ty) || 1;
    out[i] = { x: -ty / len, y: tx / len };
  }
  return out;
}

export function offsetRing(pts: Vec2[], norm: Vec2[], amount: number): Vec2[] {
  return pts.map((p, i) => ({ x: p.x + norm[i].x * amount, y: p.y + norm[i].y * amount }));
}

/**
 * Reject tracks that fold back onto themselves. Rather than an O(n^2) segment
 * intersection sweep, test whether any two points far apart *along* the track
 * end up close together *in space* — that catches crossings and near-misses in
 * one cheap distance check.
 */
export function selfIntersects(pts: Vec2[], spacing: number, clearance: number): boolean {
  const n = pts.length;
  const skip = Math.ceil(clearance / spacing) + 2;
  for (let i = 0; i < n; i++) {
    for (let j = i + skip; j < n; j++) {
      if (n - (j - i) < skip) continue; // still adjacent the other way round
      const dx = pts[i].x - pts[j].x;
      const dy = pts[i].y - pts[j].y;
      if (dx * dx + dy * dy < clearance * clearance) return true;
    }
  }
  return false;
}

export function ringLength(pts: Vec2[]): number {
  const n = pts.length;
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[wrapIndex(i + 1, n)];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}
